import { uniqueArray } from "@msuite/utils/array";
import { generateId } from "@msuite/utils/strings";
import {
	type ColumnOrderState,
	type ColumnPinningState,
	type ColumnSizingState,
	type ColumnVisibilityState,
	type ExpandedState,
	type GroupingState,
	type RowSelectionState,
	type SortingState,
	useTable,
} from "@tanstack/react-table";
import { AnimatePresence, motion } from "motion/react";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { cn } from "../../utils";
import { Table } from "../table";
import { DataTableContext } from "./context";
import { TableBody } from "./data-table-body";
import { DataTableButton } from "./data-table-button";
import { DataTableFooter } from "./data-table-footer";
import { DataTableHeader } from "./data-table-header";
import { DataTableInput } from "./data-table-input";
import { DataTableMenu } from "./data-table-menu";
import { DataTablePersister } from "./data-table-persister";
import { DataTableProgressMeter } from "./data-table-progress-meter";
import { RowSelectionHandler } from "./data-table-row-selection-handler";
import { DataTableScrollHandler } from "./data-table-scroll-handler";
import { DataTableSearch } from "./data-table-search";
import { DataTableSelect } from "./data-table-select";
import { dataTableFeatures } from "./features";
import { getPersistedColumnPinning, getPersistenceState } from "./handlers";
import type { DataTableItem, DataTableProps } from "./shared";
import type { ColumnDef, DataTableInstance, Row } from "./types";

/** A stable empty list: a fresh `[]` per render would reprocess every row model each time */
const EMPTY_DATA: any[] = [];

/** Returning the previous state when nothing changed lets React bail out of the re-render */
function isSameExpandedState(prev: ExpandedState, next: Record<string, boolean>): boolean {
	if (prev === true) return false;
	const prevKeys = Object.keys(prev);
	const nextKeys = Object.keys(next);
	if (prevKeys.length !== nextKeys.length) return false;
	for (const key of nextKeys) {
		if (prev[key] !== next[key]) return false;
	}
	return true;
}

function DataTable<T extends DataTableItem>({
	data,
	columns: _columns,
	initialSortingState = [],
	initialColumnVisibility = {},
	initialPaginationState = {
		pageSize: 25,
		pageIndex: 0,
	},
	initialPinningState = {
		start: ["row-selection", "sub-row-handle"],
		end: ["actions"],
	},
	initialColumnFilter = [],
	initialShowGroupingHeader = true,
	initialRowSelectionState = {},
	persistenceKey,
	isLoading,
	queryKey,
	grouping: _grouping = [],
	enableSorting = true,
	menu = null,
	refetch,
	renderGroupHeader,
	enableRowSelection = false,
	enableMultiRowSelection = false,
	onRowSelectionChange,
	getRowStyles,
	menuItems = [],
	slots,
	injectRowFilter,
	getRowCanExpand,
	renderSubRows,
	expandSubRowsByDefault = false,
	variant = "table",
	enableSearch = true,
}: DataTableProps<T>) {
	/** Memo */
	const columns = useMemo(() => {
		if (!enableRowSelection) return _columns as ColumnDef<T, any>[];
		return (RowSelectionHandler as ColumnDef<T, any>[]).concat(_columns);
	}, [_columns, enableRowSelection]);

	/** Refs */
	const tableContainerRef = useRef<HTMLDivElement>(null);
	const tableBoxRef = useRef<HTMLDivElement>(null);

	/** State */
	const [showGroupingHeader, setShowGroupingHeader] = useState(() =>
		getPersistenceState(persistenceKey, "showGroupingHeader", initialShowGroupingHeader),
	);
	const [isMonospaced, setIsMonospaced] = useState<boolean>(
		() => (localStorage.getItem("table-is-monospaced") || "false") === "true",
	);
	const [itemRenderId, setItemRenderId] = useState(() => generateId());
	const [expanded, setExpanded] = useState<ExpandedState>(true);
	const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(() =>
		getPersistenceState(persistenceKey, "order", []),
	);
	const [rowSelection, setRowSelection] = useState<RowSelectionState>(initialRowSelectionState);
	const [grouping, setGrouping] = useState<GroupingState>(_grouping || []);
	const [globalFilter, setGlobalFilter] = useState("");
	const [sorting, setSorting] = useState<SortingState>(() =>
		getPersistenceState(persistenceKey, "sorting", initialSortingState),
	);
	const [_columnPinning, setColumnPinning] = useState<ColumnPinningState>(() =>
		getPersistedColumnPinning(persistenceKey, {
			start: uniqueArray(["row-selection", "sub-row-handle", ...(initialPinningState.start ?? [])]),
			end: uniqueArray([...(initialPinningState.end ?? []), "actions"]),
		}),
	);
	const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() =>
		getPersistenceState(persistenceKey, "sizing", {
			"sub-row-handle": 40,
		}),
	);
	const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>(() =>
		getPersistenceState(persistenceKey, "visibility", initialColumnVisibility),
	);
	const [pagination, setPagination] = useState(() =>
		getPersistenceState(persistenceKey, "pagination", initialPaginationState),
	);
	const [columnFilters, setColumnFilters] = useState(() =>
		getPersistenceState(persistenceKey, "columnFilters", initialColumnFilter),
	);
	const [shouldRender, setShouldRender] = useState(false);

	const columnPinning = useMemo<ColumnPinningState>(() => {
		const start = [..._columnPinning.start].sort((a, b) =>
			a === "row-selection" ? -1 : b === "row-selection" ? 1 : 0,
		);
		const end = [..._columnPinning.end].sort((a, b) =>
			a === "actions" ? 1 : b === "actions" ? -1 : 0,
		);
		return { start, end };
	}, [_columnPinning]);

	const table = useTable({
		features: dataTableFeatures,
		data: data ?? EMPTY_DATA,
		columns,

		state: {
			sorting,
			columnPinning,
			globalFilter,
			columnSizing,
			columnVisibility,
			pagination,
			columnFilters,
			grouping,
			expanded,
			rowSelection,
			columnOrder,
		},

		enableRowSelection,
		enableMultiRowSelection: enableMultiRowSelection,
		enableGlobalFilter: enableSearch,

		onColumnVisibilityChange: setColumnVisibility,
		onSortingChange: setSorting,
		onColumnPinningChange: setColumnPinning,
		onGlobalFilterChange: setGlobalFilter,
		onColumnSizingChange: setColumnSizing,
		onPaginationChange: setPagination,
		onColumnFiltersChange: setColumnFilters,
		onGroupingChange: setGrouping,
		onRowSelectionChange: setRowSelection,
		onColumnOrderChange: setColumnOrder,
		onExpandedChange: setExpanded,

		globalFilterFn: "includesString",

		getRowId: (row) => row.id,
		getRowCanExpand,

		enableSorting,
		enableGrouping: true,
		autoResetExpanded: false,

		columnResizeMode: "onChange",
	});

	const columnSizes = useMemo(() => {
		const headers = table.getFlatHeaders();
		const colSizes: { [key: string]: number } = {};
		for (let i = 0; i < headers.length; i++) {
			const header = headers[i];
			if (!header) continue;
			colSizes[`--header-${header.id}-size`] = header.getSize();
			colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
		}
		return colSizes;
	}, [table, columnSizing, columnVisibility, columnOrder, columnPinning]);

	useLayoutEffect(() => {
		setTimeout(() => {
			setShouldRender(true);
		}, 0);
	}, []);

	useEffect(() => {
		if (onRowSelectionChange) {
			onRowSelectionChange(rowSelection);
		}
	}, [rowSelection]);

	useEffect(() => {
		if (expandSubRowsByDefault) return;
		const groupRows: Record<string, boolean> = {};
		for (const row of table.getRowModel().flatRows) {
			if (row.getIsGrouped()) groupRows[row.id] = true;
		}
		setExpanded((prev) => (isSameExpandedState(prev, groupRows) ? prev : groupRows));
	}, [data, expandSubRowsByDefault]);

	const handleOnSetItemRenderId = useCallback(() => {
		setItemRenderId(generateId());
	}, []);

	const getSelectedRowIds = useCallback(() => {
		return uniqueArray(table.getSelectedRowModel().rows.map((row) => row.id));
	}, [table]);

	const handleOnToggleMonospaced = useCallback(() => {
		const current = localStorage.getItem("table-is-monospaced") || "false";
		if (current === "false") {
			localStorage.setItem("table-is-monospaced", "true");
			setIsMonospaced(true);
		} else {
			localStorage.setItem("table-is-monospaced", "false");
			setIsMonospaced(false);
		}
	}, []);

	/** Memoized context value */
	const contextValue = useMemo(
		() => ({
			table: table as DataTableInstance<any>,
			sorting,
			persistenceKey,
			queryKey,
			refetch,
			tableContainerRef,
			tableBoxRef,
			columnSizes,
			hasRowSelection: table.getSelectedRowModel().rows.length > 0,
			rowSelection,
			itemRenderId,
			handleOnSetItemRenderId,
			getSelectedRowIds,
			getRowStyles: getRowStyles as (row: Row<any>) => React.CSSProperties,
			isMonospaced,
			handleOnToggleMonospaced,
			injectRowFilter: injectRowFilter as (row: Array<Row<any>>) => Array<Row<any>>,
			showGroupingHeader,
			setShowGroupingHeader,
			renderSubRows: renderSubRows as (row: Row<any>) => React.ReactNode | undefined,
			variant,
		}),
		[
			table,
			sorting,
			rowSelection,
			persistenceKey,
			queryKey,
			refetch,
			tableContainerRef,
			tableBoxRef,
			columnSizes,
			itemRenderId,
			getRowStyles,
			isMonospaced,
			showGroupingHeader,
			renderSubRows,
			variant,
			injectRowFilter,
			handleOnSetItemRenderId,
			getSelectedRowIds,
			handleOnToggleMonospaced,
		],
	);

	/** Render */
	return (
		<AnimatePresence>
			{shouldRender && (
				<motion.div
					className={cn(
						"flex flex-col shrink gap-4 h-full",
						variant === "sub-table" && "p-4 h-[600px] stripes",
					)}
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.15 }}
				>
					<DataTableContext.Provider value={contextValue}>
						<DataTablePersister />
						<DataTableScrollHandler
							tableContainerRef={tableContainerRef}
							isLoading={isLoading}
						/>
						{slots?.["above-search"]?.()}
						<div className="hidden md:flex flex-row justify-between items-center w-full h-stack">
							<div className="h-stack flex-1 gap-4">
								{slots?.["instead-of-search"] ? (
									slots?.["instead-of-search"]()
								) : (
									<DataTableSearch />
								)}
								{slots?.["right-of-search"]?.()}
							</div>
							<div className="flex flex-row items-center gap-4">
								{menu ? (typeof menu === "function" ? menu() : menu) : null}
								<DataTableMenu menuItems={menuItems} />
							</div>
						</div>
						{slots?.["below-search"]?.()}
						<Table
							Footer={DataTableFooter}
							ref={tableContainerRef}
							isLoading={isLoading}
						>
							<DataTableHeader setColumnOrder={setColumnOrder} />
							<TableBody
								table={table}
								renderGroupHeader={renderGroupHeader}
							/>
						</Table>
					</DataTableContext.Provider>
				</motion.div>
			)}
		</AnimatePresence>
	);
}

DataTable.Select = DataTableSelect;
DataTable.Button = DataTableButton;
DataTable.Input = DataTableInput;
DataTable.ProgressMeter = DataTableProgressMeter;

export { DataTable };
